<template>
  <div class="taxSettingsModal">
    <Modal v-if="open" @hide-modal="closeModal">
      <template #header>
        <div class="p-4">
          <slot name="header"></slot>
        </div>
      </template>
      <TaxSettings
        v-if="open"
        :key="taxSettingsRenderKey"
        ref="taxSettingsForm"
        :context="context"
        :context-type="contextType"
        :settings="settings"
        :is-editing="isEditing"
        @show-edit-tax-settings="handleShowEditTaxSettings"
      />
      <template #footer>
        <div class="flex py-4 px-4 md:px-8">
          <SeraButton
            v-if="isEditing"
            type="secondary"
            text="Cancel"
            data-cy="cancel-edit"
            :disabled="isSaving"
            @click="handleCancelEditTaxSettings"
          />
          <SeraButton
            v-if="isEditing"
            class="ml-auto"
            :text="isSaving ? 'Updating...' : 'Save'"
            :disabled="isSaving"
            data-cy="tax-settings-save"
            @click="handleSave"
          />
        </div>
      </template>
    </Modal>
  </div>
</template>

<script lang="ts" setup>
import { ref } from "vue";

import SeraButton from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import { TaxSettingsContextTypes, TaxSettingsData } from "@/typings/taxability";
import store from "@/vuex/store";
import TaxSettings from "./TaxSettings.vue";

const props = defineProps({
  context: {
    type: [String, Number, Object],
    default: null,
    required: false
  },
  contextType: {
    type: String as () => TaxSettingsContextTypes,
    default: null,
    required: true
  },
  gid: {
    type: String,
    default: ""
  },
  open: {
    type: Boolean,
    default: false
  },
  settings: {
    type: Object as () => TaxSettingsData,
    default: () => {},
    required: true
  }
});
const emit = defineEmits(["close", "refresh"]);

const taxSettingsForm = ref<any>(null);
const isEditing = ref(false);
const isSaving = ref(false);
const taxSettingsRenderKey = ref(1);

function closeModal() {
  isEditing.value = false;
  emit("close");
}

function handleShowEditTaxSettings() {
  isEditing.value = true;
}

function handleCancelEditTaxSettings() {
  isEditing.value = false;
}

async function handleSave() {
  const data = await taxSettingsForm.value.getFormData();
  try {
    isSaving.value = true;
    await store.dispatch("updateTaxSettings", {
      ...data,
      configurable_gid: props.gid
    });
    window.notify.success("Tax settings saved.");
    dispatchRefresh();
  } catch (e: any) {
    // Do nothing, errors notify by default
  }
  isSaving.value = false;
}

function dispatchRefresh() {
  isEditing.value = false;
  taxSettingsRenderKey.value = taxSettingsRenderKey.value + 1;
  emit("refresh");
}
</script>
